import React, { createContext, PureComponent } from 'react'
import PropTypes from 'prop-types'

import { getRandomId } from 'common/helpers'


const { Provider, Consumer } = createContext()

class Menu extends PureComponent {

  menuRefs = {
    button: null,
    list: null,
    items: [],
  }

  state = {
    isOpen: false,
    isKeyDown: false,
    buttonRect: null,
    selectionIndex: -1,
    buttonId: getRandomId('button'),
    closingWithClick: false,
  }

  getSnapshotBeforeUpdate(prevProps, prevState) {
    const { isOpen } = this.state

    if (!isOpen && prevState.isOpen) {
      // if we closed menu while been focused on menu list or not
      return this.menuRefs.list.contains(document.activeElement)
    }

    return null
  }

  componentDidUpdate(prevProps, prevState) {
    const { isOpen, selectionIndex, isKeyDown } = this.state

    if (isOpen && !prevState.isOpen) {
      if (selectionIndex !== -1) {
        // haven't measured the popover yet, give it a frame otherwise
        // we'll scroll to the bottom of the page >.<
        requestAnimationFrame(() => {
          if (this.menuRefs.items[selectionIndex] && isKeyDown) {
            this.menuRefs.items[selectionIndex].focus()
          }
        })
      }
      else {
        this.menuRefs.list.focus()
      }
    }
    else if (!isOpen && prevState.isOpen) {
      if (isKeyDown) {
        this.menuRefs.button.focus()
      }
    }
    else if (selectionIndex !== prevState.selectionIndex) {
      if (selectionIndex === -1) {
        // when mousing over non-menu list items focus on the menu list
        // so the the keyboard will work after a mouseover
        this.menuRefs.list.focus()
      }
      else {
        if (this.menuRefs.items[selectionIndex]) {
          this.menuRefs.items[selectionIndex].focus()
        }
      }
    }
  }

  setMenuState = (newState) => {
    this.setState(newState)
  }

  open = ({ isKeyDown = false } = {}) => {
    this.setState({
      isOpen: true,
      isKeyDown,
      selectionIndex: 0,
    })
  }

  close = ({ isKeyDown = false } = {}) => {
    this.setState({
      isOpen: false,
      isKeyDown,
      selectionIndex: -1,
      closingWithClick: false,
    })
  }

  handleMenuClick = () => {
    const { isOpen } = this.state

    if (isOpen) {
      this.close()
    }
    else {
      this.open()
    }
  }

  handleMenuKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter') {
      event.preventDefault()

      this.open({ isKeyDown: true })
    }
  }

  handleMenuMouseDown = () => {
    const { isOpen } = this.state

    if (isOpen) {
      this.setState({
        closingWithClick: true,
      })
    }
  }

  handleMenuMouseEnter = () => {
    const { toggleOnHover } = this.props

    if (toggleOnHover) {
      this.open()
    }
  }

  handleMenuMouseLeave = () => {
    const { toggleOnHover } = this.props

    if (toggleOnHover) {
      setTimeout(() => {
        if (this.menuRefs.list && !this.menuRefs.list.getAttribute('data-menulist-hovered')) {
          this.close()
        }
      })
    }
  }

  handleMenuBlur = () => {
    const { closeOnBlur } = this.props

    if (closeOnBlur) {
      this.close()
    }
  }

  handleListBlur = (event) => {
    const { closingWithClick } = this.state

    if (!closingWithClick && !this.menuRefs.list.contains(event.relatedTarget)) {
      this.close()
    }
  }

  handleListKeyDown = (event) => {
    const { selectionIndex } = this.state

    if (event.key === 'Escape') {
      this.close({ isKeyDown: true })
    }
    else if (event.key === 'ArrowDown') {
      event.preventDefault() // prevent window scroll

      const nextIndex = selectionIndex + 1

      if (nextIndex !== this.menuRefs.items.length) {
        this.setState({ selectionIndex: nextIndex })
      }
    }
    else if (event.key === 'ArrowUp') {
      event.preventDefault() // prevent window scroll

      const nextIndex = selectionIndex - 1

      if (nextIndex !== -1) {
        this.setState({
          selectionIndex: nextIndex,
        })
      }
    }
    else if (event.key === 'Tab') {
      this.menuRefs.button.focus()
    }
  }

  handleListMouseEnter = () => {
    if (this.menuRefs.list) {
      this.menuRefs.list.setAttribute('data-menulist-hovered', true)
    }
  }

  handleListMouseLeave = () => {
    const { toggleOnHover } = this.props

    if (toggleOnHover) {
      this.close()
      this.menuRefs.list.removeAttribute('data-menulist-hovered')
    }
  }

  getListStyles = (listRect) => {
    const { buttonRect } = this.state
    const { fullWidth } = this.props

    if (!buttonRect) {
      return {
        opacity: 0,
      }
    }

    const styles = {
      position: 'absolute',
      zIndex: 200,
      left: `${buttonRect.left}px`,
      top: `${buttonRect.top + buttonRect.height}px`,
    }

    if (!listRect) {
      return {
        ...styles,
        opacity: 0,
      }
    }

    if (buttonRect.width < 500) {
      styles.minWidth = buttonRect.width
    }

    const collisions = {
      top: buttonRect.top - listRect.height < 0,
      right: window.innerWidth < buttonRect.left + listRect.width,
      bottom: window.innerHeight < buttonRect.top + listRect.height,
      left: buttonRect.left - listRect.width < 0,
    }

    const directionRight = collisions.right && !collisions.left
    const directionUp    = collisions.bottom && !collisions.top

    if (fullWidth) {
      return {
        ...styles,
        left: '50%',
        top: directionUp
          ? `${buttonRect.top - listRect.height + window.scrollY}px`
          : `${buttonRect.top + buttonRect.height + window.scrollY}px`,
        width: '96%',
        maxWidth: '1240px',
        transform: 'translate3d(-50%, 0, 0)',
      }
    }

    return {
      ...styles,
      left: directionRight
        ? `${buttonRect.right - listRect.width + window.scrollX}px`
        : `${buttonRect.left + window.scrollX}px`,
      top: directionUp
        ? `${buttonRect.top - listRect.height + window.scrollY}px`
        : `${buttonRect.top + buttonRect.height + window.scrollY}px`,
    }
  }

  render() {
    const { children } = this.props
    const { isOpen } = this.state

    return (
      <Provider
        value={{
          refs: this.menuRefs,
          state: this.state,
          setState: this.setMenuState,

          handleMenuBlur: this.handleMenuBlur,
          handleMenuClick: this.handleMenuClick,
          handleMenuKeyDown: this.handleMenuKeyDown,
          handleMenuMouseDown: this.handleMenuMouseDown,
          handleMenuMouseEnter: this.handleMenuMouseEnter,
          handleMenuMouseLeave: this.handleMenuMouseLeave,

          handleListBlur: this.handleListBlur,
          handleListKeyDown: this.handleListKeyDown,
          handleListMouseEnter: this.handleListMouseEnter,
          handleListMouseLeave: this.handleListMouseLeave,
          getListStyles: this.getListStyles,
        }}
      >
        {
          typeof children === 'function'
            ? children({ isOpen })
            : children
        }
      </Provider>
    )
  }
}

Menu.propTypes = {
  children: PropTypes.oneOfType([ PropTypes.func, PropTypes.node ]),
  toggleOnHover: PropTypes.bool,
  closeOnBlur: PropTypes.bool,
  fullWidth: PropTypes.bool,
}

Menu.defaultProps = {
  toggleOnHover: false,
  closeOnBlur: false,
  fullWidth: false,
}


export { Consumer }

export default Menu
